import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { captureEvent } from '@/lib/posthog';
import { track } from '@/lib/analytics';
import type { Shift } from '@/types/caregiver';


interface CheckInOptions {
  patientId: string;
  photoUrl?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  shiftTemplateId?: string | null;
  isSwap?: boolean;
  originalTemplateId?: string | null;
  taskInstances?: { taskId: string; scheduledFor: string }[];
}

interface ShiftContextType {
  activeShift: Shift | null;
  isLoading: boolean;
  checkIn: (options: CheckInOptions) => Promise<Shift | null>;
  checkOut: (photoUrl?: string | null) => Promise<boolean>;
  forceCheckOut: (shiftId: string, patientId: string, adminNote: string) => Promise<boolean>;
  refreshShift: () => Promise<void>;
}

export const ShiftContext = createContext<ShiftContextType | undefined>(undefined);

// ISO week key: YYYY-Www (e.g. 2026-W18). Used to dedupe per-week milestones.
function isoWeekKey(d: Date): string {
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target.getTime() - firstThursday.getTime()) / 86400000;
  const week = 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${target.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

async function fanoutShiftEvent(
  event: 'shift_started' | 'shift_ended',
  props: { patient_id: string; shift_id: string; caregiver_id: string | null; is_swap?: boolean },
) {
  try {
    const { data: links } = await supabase
      .from('patient_user_links')
      .select('user_id')
      .eq('patient_id', props.patient_id)
      .eq('role', 'owner')
      .eq('active', true);
    const owners = (links ?? [])
      .map((l) => l.user_id as string)
      .filter((id) => id && id !== props.caregiver_id);
    await Promise.all(
      owners.map((ownerId) =>
        supabase.functions.invoke('analytics-emit', {
          body: {
            distinct_id: ownerId,
            event,
            properties: { ...props, source: 'fanout', actor_user_id: props.caregiver_id },
          },
        }),
      ),
    );
  } catch (e) {
    console.warn(`[analytics] ${event} fan-out failed`, e);
  }
}

async function emitCompanySchedulePublishedMilestone(opts: {
  caregiverId: string | null;
  shiftId: string;
  checkInAt: Date;
}) {
  if (!opts.caregiverId) return;
  try {
    const { data: membership } = await supabase
      .from('company_members')
      .select('company_id')
      .eq('user_id', opts.caregiverId)
      .eq('is_active', true)
      .limit(1)
      .maybeSingle();
    const companyId = membership?.company_id as string | undefined;
    if (!companyId) return;
    const week = isoWeekKey(opts.checkInAt);
    await supabase.functions.invoke('analytics-emit', {
      body: {
        distinct_id: companyId,
        event: 'company_schedule_published',
        milestone: `company_schedule_published_${companyId}_${week}`,
        properties: {
          company_id: companyId,
          iso_week: week,
          first_shift_id: opts.shiftId,
          caregiver_id: opts.caregiverId,
          source: 'fanout',
        },
      },
    });
  } catch (e) {
    console.warn('[analytics] company_schedule_published milestone failed', e);
  }
}


export function ShiftProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeShift, setActiveShift] = useState<Shift | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchActiveShift();
    } else {
      setActiveShift(null);
      setIsLoading(false);
    }
  }, [user]);

  const fetchActiveShift = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('shifts')
        .select('*')
        .eq('caregiver_id', user.id)
        .eq('status', 'active')
        .order('check_in_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      setActiveShift(data as Shift | null);
    } catch (error) {
      console.error('Error fetching active shift:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkIn = async (options: CheckInOptions): Promise<Shift | null> => {
    if (!user) return null;

    const { patientId, photoUrl, latitude, longitude, shiftTemplateId, isSwap, originalTemplateId } = options;

    console.log('ShiftContext: checkIn called with:', {
      patientId,
      shiftTemplateId,
      isSwap
    });

    try {
      // Check if ANYONE has an active shift for this patient
      const { data: existingShift } = await supabase
        .from('shifts')
        .select('*')
        .eq('patient_id', patientId)
        .eq('status', 'active')
        .maybeSingle();

      if (existingShift) {
        console.error('An active shift already exists for this patient [Strict Block]');
        // PostHog Tracking for blocked attempt
        captureEvent('caregiver_check_in_blocked', {
          patient_id: patientId,
          reason: 'active_shift_exists',
          is_swap: isSwap
        });
        return null; // Strict block: User must check out first
      }


      const insertData: any = {
        patient_id: patientId,
        caregiver_id: user.id,
        status: 'active'
      };

      if (photoUrl) {
        insertData.check_in_photo_url = photoUrl;
      }

      if (latitude !== null && latitude !== undefined) {
        insertData.check_in_latitude = latitude;
      }

      if (longitude !== null && longitude !== undefined) {
        insertData.check_in_longitude = longitude;
      }

      // Handle shift template assignment
      if (shiftTemplateId) {
        insertData.shift_template_id = shiftTemplateId;
      }

      // Handle shift swap - NO LONGER CLOSES AUTOMATICALLY
      if (isSwap) {
        insertData.is_swap = true;
        if (originalTemplateId) {
          insertData.original_template_id = originalTemplateId;
        }
      }


      const { data, error } = await supabase
        .from('shifts')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      const shift = data as Shift;

      // Instantiate tasks for this shift
      if (options.taskInstances && options.taskInstances.length > 0) {
        console.log(`ShiftContext: Instantiating ${options.taskInstances.length} client-provided tasks`);
        const instancesToInsert = options.taskInstances.map(inst => ({
          shift_id: shift.id,
          task_template_id: inst.taskId,
          patient_id: patientId,
          scheduled_for: inst.scheduledFor,
          status: 'pending'
        }));

        const { error: batchError } = await supabase
          .from('shift_task_instances')
          .insert(instancesToInsert);

        if (batchError) {
          console.error('Error inserting client-provided task instances:', batchError);
        }
      } else {
        // Fallback to RPC if no explicit instances provided
        const checkInTime = new Date().toISOString();
        const { data: instancesCount, error: instanceError } = await supabase
          .rpc('instantiate_shift_tasks', {
            p_shift_id: shift.id,
            p_patient_id: patientId,
            p_check_in_time: checkInTime,
            p_shift_template_id: shiftTemplateId || null
          });

        if (instanceError) {
          console.error('Error instantiating tasks via RPC:', instanceError);
        } else {
          console.log(`Created ${instancesCount} task instances for shift via RPC`);
        }
      }

      setActiveShift(shift);

      // PostHog Tracking
      captureEvent('caregiver_check_in', {
        patient_id: patientId,
        shift_id: shift.id,
        is_swap: isSwap,
        has_location: !!latitude && !!longitude
      });

      // `shift_started` for the caregiver is emitted server-side (trigger on shifts).

      // Fan-out shift_started to patient owners (server-side via analytics-emit).
      fanoutShiftEvent('shift_started', {
        patient_id: patientId,
        shift_id: shift.id,
        is_swap: isSwap,
        caregiver_id: user?.id ?? null,
      });

      // Company schedule published — first shift of the ISO week for the caregiver's company.
      void emitCompanySchedulePublishedMilestone({
        caregiverId: user?.id ?? null,
        shiftId: shift.id,
        checkInAt: new Date(),
      });

      // Idempotent first_shift_started (server-side milestone, cross-device).
      // analytics-emit dedupes via public.analytics_milestones (UNIQUE user_id+milestone).
      try {
        if (user?.id) {
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;
          if (token) {
            await supabase.functions.invoke('analytics-emit', {
              body: {
                distinct_id: user.id,
                event: 'first_shift_started',
                milestone: 'first_shift_started',
                properties: {
                  patient_id: patientId,
                  shift_id: shift.id,
                  is_swap: isSwap,
                },
              },
            });
            // Also fire client-side for PostHog session/super-properties context.
            // Server dedupes; client may double-fire across devices but PostHog
            // can be deduped downstream via $insert_id if needed.
            const flagKey = `careos_first_shift_started_${user.id}`;
            if (!localStorage.getItem(flagKey)) {
              track('first_shift_started', {
                patient_id: patientId,
                shift_id: shift.id,
                is_swap: isSwap,
              });
              localStorage.setItem(flagKey, new Date().toISOString());
            }
          }
        }
      } catch (e) {
        console.warn('[analytics] first_shift_started milestone failed', e);
      }

      return shift;

    } catch (error) {
      console.error('Error checking in:', error);
      return null;
    }
  };

  const checkOut = async (photoUrl?: string | null): Promise<boolean> => {
    if (!activeShift) return false;

    try {
      const updateData: any = {
        check_out_at: new Date().toISOString(),
        status: 'completed'
      };

      if (photoUrl) {
        updateData.check_out_photo_url = photoUrl;
      }

      const { error } = await supabase
        .from('shifts')
        .update(updateData)
        .eq('id', activeShift.id);

      if (error) throw error;

      setActiveShift(null);

      // PostHog Tracking
      captureEvent('caregiver_check_out', {
        patient_id: activeShift.patient_id,
        shift_id: activeShift.id,
        has_photo: !!photoUrl
      });
      // `shift_ended` for the caregiver is emitted server-side (trigger on shifts).

      // Fan-out shift_ended to patient owners.
      fanoutShiftEvent('shift_ended', {
        patient_id: activeShift.patient_id,
        shift_id: activeShift.id,
        caregiver_id: user?.id ?? null,
      });

      return true;

    } catch (error) {
      console.error('Error checking out:', error);
      return false;
    }
  };

  const forceCheckOut = async (shiftId: string, patientId: string, adminNote: string): Promise<boolean> => {
    try {
      console.log(`Force checking out shift ${shiftId} for patient ${patientId} with note: ${adminNote}`);

      // 1. Fetch stats for the handoff record
      const { data: executions } = await supabase
        .from('task_executions')
        .select('*')
        .eq('shift_id', shiftId);

      const incidentsCount = executions?.filter(e => e.adhoc_task_type === 'incident').length || 0;
      const sporadicMedsCount = executions?.filter(e =>
        e.adhoc_task_type === 'sporadic_medication' || (e.task_id && e.is_sporadic)
      ).length || 0;

      const { data: instances } = await supabase
        .from('shift_task_instances')
        .select('status')
        .eq('shift_id', shiftId);

      const totalTasks = instances?.length || 0;
      const completedTasks = instances?.filter(i => i.status === 'completed').length || 0;

      // 2. Create handoff record
      const { data: shiftData } = await supabase
        .from('shifts')
        .select('caregiver_id')
        .eq('id', shiftId)
        .single();

      const { error: handoffError } = await supabase
        .from('handoffs')
        .insert({
          shift_id: shiftId,
          patient_id: patientId,
          from_caregiver_id: shiftData?.caregiver_id || user?.id || '',
          text_summary: adminNote,
          tasks_completed: completedTasks,
          tasks_total: totalTasks,
          sporadic_meds_given: sporadicMedsCount,
          incidents_count: incidentsCount,
          confirmed_at: new Date().toISOString()
        });

      if (handoffError) throw handoffError;

      // 3. Complete the shift
      const { error: shiftError } = await supabase
        .from('shifts')
        .update({
          check_out_at: new Date().toISOString(),
          status: 'completed',
          notes: adminNote
        })
        .eq('id', shiftId);

      if (shiftError) throw shiftError;

      // 4. Trigger AI summary in background
      supabase.functions.invoke('generate-shift-summary', {
        body: { shiftId, patientId }
      }).catch(err => console.error('AI Summary trigger failed:', err));

      // 5. Refresh local state if the current user was the one in shift
      if (activeShift?.id === shiftId) {
        setActiveShift(null);
      } else {
        await refreshShift();
      }

      // PostHog Tracking
      captureEvent('admin_force_check_out', {
        patient_id: patientId,
        shift_id: shiftId,
        note: adminNote
      });

      return true;
    } catch (error) {
      console.error('Error force checking out:', error);
      return false;
    }
  };

  const refreshShift = async () => {
    await fetchActiveShift();
  };

  return (
    <ShiftContext.Provider value={{
      activeShift,
      isLoading,
      checkIn,
      checkOut,
      forceCheckOut,
      refreshShift
    }}>
      {children}
    </ShiftContext.Provider>
  );
}

